import { ErrorCode, FileRejection } from "react-dropzone";

export interface MultipleFile {
    [key: string]: File[];
}

export interface Document {
    /** file content as base64 string */
    content: string;
    fileName: string;
    mimeType: string;
}

/**
  * Transforms given size string to bytes.
  *
  * @param size given as "{value} {unit}" string, e.g. "10 MB"
  supported units: KiB, MiB, GiB, TiB, KB, MB, GB, TB
  * @returns number: given size in bytes
  */
export const toByteConverter = (size: string): number => {
    const splitSize = size.split(/\s+/);
    const value = parseFloat(splitSize[0]);
    const unit = splitSize[1];

    function toBytes(n: number, base: number, power: number) {
        return n * Math.pow(base, power);
    }

    switch (unit) {
        case "KiB":
            return toBytes(value, 2, 10);
        case "MiB":
            return toBytes(value, 2, 20);
        case "GiB":
            return toBytes(value, 2, 30);
        case "TiB":
            return toBytes(value, 2, 40);
        case "KB":
            return toBytes(value, 10, 3);
        case "MB":
            return toBytes(value, 10, 6);
        case "GB":
            return toBytes(value, 10, 9);
        case "TB":
            return toBytes(value, 10, 12);
        default:
            return value;
    }
};

/**
 * Transforms given bytes to a string with
 * a more human-readable size unit.
 *
 * @param bytes bytes to convert to size string
 * @returns bytes as a {value} {unit} string
 */
export const toSizeStringConverter = (bytes: number): string => {
    const units = ["Bytes", "KB", "MB", "GB", "TB"];

    let unitIndex = 0;
    let remainingBytes = bytes;
    while (remainingBytes >= 1000 && ++unitIndex) {
        remainingBytes /= 1000;
    }

    return `${remainingBytes % 1 === 0 ? remainingBytes.toFixed(0) : remainingBytes.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * Filters out duplicate files from the given files.
 * A file is considered the same based on its name and type.
 *
 * @param newFiles files to filter
 * @param referenceFiles files to check against for duplicates
 */
export const filterDuplicateFiles = (newFiles: File[], referenceFiles: File[]): File[] => {
    return newFiles.filter(
        (file) => referenceFiles.filter((currFile) => currFile.name === file.name && currFile.type === file.type).length === 0,
    );
};

/**
 * Extracts unique error codes from file rejections.
 *
 * @param rejectedFiles array of FileRejection objects
 * @returns an array of unique FileError ErrorCode enums
 */
export const collectFileRejectionErrors = (rejectedFiles: FileRejection[]) => {
    let errors: ErrorCode[] = [];
    const errorCodes = rejectedFiles.map((rejectedFile) => rejectedFile.errors.map((err) => err.code as ErrorCode));

    for (const err of errorCodes) {
        errors = [...errors, ...err.filter((error) => !errors.includes(error))];
    }

    return errors;
};

/**
 * Converts given file to base64 string.
 */
export const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = () => {
            const result = fileReader.result;

            if (!(result === null || result instanceof ArrayBuffer)) {
                // remove data:*/*;base64, from the converted string
                resolve(result.split(";base64,")[1]);
            }

            reject(new Error("Error transforming file to base64."));
        };
        fileReader.onerror = (error) => reject(error);
    });

const fileToDocument = async (file: File): Promise<Document> => {
    try {
        const base64 = await toBase64(file);
        return { content: base64, fileName: file.name, mimeType: file.type };
    } catch (error) {
        console.log("Error for file: " + file.name);
        return Promise.reject(error);
    }
};

export const mapMultipleFilesToDocuments = async (fileUploadSections: MultipleFile): Promise<Document[]> => {
    const promises = [];
    for (const fileUploadSectionKey in fileUploadSections) {
        const allCurrentFiles = fileUploadSections[fileUploadSectionKey];
        promises.push(mapFilesToDocuments(allCurrentFiles));
    }

    const documentsArrays = await Promise.all(promises);
    return documentsArrays.flat();
};

export const mapFilesToDocuments = (files: File[]): Promise<Document[]> => {
    const promises = [];
    for (const file of files) {
        promises.push(fileToDocument(file));
    }

    return Promise.all(promises);
};

/**
 * Calculates total size of the given files.
 */
export const sumFileSizes = (files: File[]): number => {
    return files.map((file) => file.size).reduce((size1, size2) => size1 + size2, 0);
};
